import { useState, useEffect, useMemo } from 'react'
import { MENU_NAMES } from 'texts/menuNames'
import { TOOLTIP, TABLE, PRICE_INFO } from 'texts/dropoffFee'
import { EditableCell, Table } from 'ui/molecules/Table'
import {
  dropOffFeeApi,
  DropOffFeeApiTypes,
  referencesApi,
  ReferencesApiTypes,
} from 'api'
import { Typography, Button, Tooltip } from '@frontend/design_system'
import { ArrowLeftRightIcon, InfoIcon } from 'ui/icons'
import { useApiRequest } from 'hooks/useApiRequest'
import { PAGINATION_DROPDOWN_LABEL } from 'texts/uiTexts'
import { breadcrumbs } from 'constants/breadcrumbs'
import { FilterRoute } from './components/FilterRoute'
import { PriceInfo } from './components/PriceInfo'
import { isOperatorSelector } from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { Container } from 'ui/molecules/Container'
import { useCompanyId } from 'hooks/useCompanyId'
import { ResetIcon } from 'ui/icons/ResetIcon'
import { RESET_ALL } from 'texts/common'
import { isDropoffFilterAppliedSelector } from 'redux/dropoff/selectors'
import {
  setDropoffFilterData,
  setIsDropoffFilterApplied,
} from 'redux/dropoff/slice'
import { useDispatch } from 'react-redux'
import { Pagination } from 'ui/molecules/Pagination'
import { usePagination } from 'hooks/usePagination'
import styles from './styles.module.scss'

export const DropoffFeeList = () => {
  const [options, setOptions] = useState<DropOffFeeApiTypes.DropOffData>({})
  const [locations, setLocations] = useState<ReferencesApiTypes.Location[]>()
  const [dropOffFeeData, setDropOffFeeData] =
    useState<DropOffFeeApiTypes.GetDropOffFeeListResponse | null>(null)
  const isOperator = useAppSelector(isOperatorSelector)
  const isDropoffFilterApplied = useAppSelector(isDropoffFilterAppliedSelector)
  const dispatch = useDispatch()
  const companyId = useCompanyId()

  const { page, pageSize, handleResetPagination } = usePagination()

  const isDisabledFilter = !(
    dropOffFeeData && dropOffFeeData?.routes.pageItems.length > 1
  )

  const dropOffFeeListRequest = useApiRequest(
    (
      page: number,
      pageSize: number,
      companyId: number,
      options: DropOffFeeApiTypes.DropOffData
    ) => dropOffFeeApi.getDropOffFeeList(page, pageSize, companyId, options)
  )

  const dropOffFeeRequest = useApiRequest(
    (
      fromHirePointId: number,
      toHirePointId: number,
      data: DropOffFeeApiTypes.SetDropOffFeeData
    ) => dropOffFeeApi.setDropOffFee(fromHirePointId, toHirePointId, data)
  )

  const getAllCompanyHirePointsRequest = useApiRequest((companyId: number) =>
    referencesApi.getAllCompanyHirePoints(String(companyId), true)
  )

  const saveRouteValue = async (
    toLocationId: number,
    fromLocationId: number,
    value?: number | string
  ) => {
    dropOffFeeRequest.apiRequest(fromLocationId, toLocationId, {
      companyId,
      fromHirePointId: fromLocationId,
      toHirePointId: toLocationId,
      price: value ? Number(value) : null,
    })
  }

  const handleRouteChange = ({
    pickUpLocation,
    dropOffLocation,
  }: {
    pickUpLocation?: string
    dropOffLocation?: string
  }) => {
    handleResetPagination()
    setOptions({
      toLocationContractId: Number(dropOffLocation),
      fromLocationContractId: Number(pickUpLocation),
    })
  }

  const handleResetFilter = () => {
    dispatch(setIsDropoffFilterApplied(false))
    dispatch(setDropoffFilterData({ pickUpLocation: '', dropOffLocation: '' }))
    handleRouteChange({ pickUpLocation: '', dropOffLocation: '' })
  }

  const formattedHeadItems = [
    {
      value: (
        <div className={styles['header-pick-up']}>
          <div className={styles['header-title-block']}>
            <Typography
              name="Subtitle3"
              className={styles['header-first-title']}
            >
              {`${TABLE.PICK_UP} - ${TABLE.DROP_OFF}`}
            </Typography>
            <ArrowLeftRightIcon size="medium" color="grey900" />
            <Typography
              name="Subtitle3"
              className={styles['header-second-title']}
            >
              {`${TABLE.PICK_UP} - ${TABLE.DROP_OFF}`}
            </Typography>
          </div>
          <div>
            <FilterRoute
              items={locations}
              onChange={handleRouteChange}
              isDisabled={isDisabledFilter}
            />
          </div>
        </div>
      ),
    },
    {
      value: (
        <div className={styles['header-price']}>
          <Typography name="Subtitle3">{`${PRICE_INFO.TITLE}`}</Typography>
          <Tooltip
            arrowPosition="center"
            popupContent={<PriceInfo />}
            placement="bottom"
          >
            <InfoIcon color="blue700" size="small" />
          </Tooltip>
        </div>
      ),
    },
  ]

  const formattedBodyItems = useMemo(
    () =>
      dropOffFeeData?.routes.pageItems.map((el) => ({
        items: [
          <div className={styles['location-info']}>
            <Typography
              name="Subtitle7"
              Tag="div"
              className={styles['from-location-item']}
            >
              {`${el.fromLocationName} - ${el.toLocationName}`}
            </Typography>
            <Typography
              name="Subtitle7"
              Tag="div"
              className={styles['to-location-item']}
            >
              {`${el.toLocationName} - ${el.fromLocationName}`}
            </Typography>
          </div>,
          <EditableCell
            value={el.price}
            handleUpdate={(price) =>
              saveRouteValue(el.toHirePointId, el.fromHirePointId, price)
            }
            dataTestId="dropoff-editable-cell"
          />,
        ],
        key: `${el.fromHirePointId}to${el.toHirePointId}`,
      })),
    [dropOffFeeData?.routes.pageItems]
  )

  useEffect(() => {
    const optionData: DropOffFeeApiTypes.DropOffData = {}
    if (!!options.fromLocationContractId) {
      optionData.fromLocationContractId = options.fromLocationContractId
    }
    if (!!options.toLocationContractId) {
      optionData.toLocationContractId = options.toLocationContractId
    }
    const getDropOffFeeDatA = async () => {
      const response = await dropOffFeeListRequest.apiRequest(
        page - 1,
        Number(pageSize),
        companyId,
        optionData
      )
      if (response && response.data) {
        setDropOffFeeData(response.data)
      }
    }
    getDropOffFeeDatA()
  }, [page, pageSize, companyId, options])

  useEffect(() => {
    const getLocationsList = async () => {
      const response = await getAllCompanyHirePointsRequest.apiRequest(
        companyId
      )
      if (response && response.data) {
        setLocations(response.data.items)
      }
    }
    getLocationsList()
  }, [companyId])

  return (
    <div>
      <Container
        breadcrumbList={isOperator ? [breadcrumbs.profileList] : []}
        currentPageLabel={MENU_NAMES.DROP_OFF_FEE}
        title={MENU_NAMES.DROP_OFF_FEE}
        withNavigation={isOperator}
        tooltip={TOOLTIP}
        loading={!dropOffFeeListRequest.isLoaded}
      >
        <div className={styles.content}>
          <div className={styles.list}>
            <div className={styles.header}>
              <div className={styles['action-buttons']}>
                {isDropoffFilterApplied && (
                  <Button
                    label={RESET_ALL}
                    variant="secondary"
                    typographyName="Subtitle3"
                    onClick={handleResetFilter}
                    dataTestId="reset-filters-button"
                    size="small"
                    iconLeft={<ResetIcon color="blue700" size="small" />}
                  />
                )}
              </div>
            </div>
            <div className={styles.table}>
              {formattedBodyItems && (
                <Table
                  headItems={formattedHeadItems}
                  bodyItems={formattedBodyItems}
                  initItemsWidth={['653', '200']}
                  dataTestId="dropoff-fee-table"
                />
              )}
            </div>
          </div>
          <footer className={styles.pagination}>
            <Pagination
              defaultPage={page}
              defaultPageSize={Number(pageSize)}
              pageItemCount={dropOffFeeData?.routes.totalPages || 0}
              total={dropOffFeeData?.routes.totalItems || 0}
              pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
            />
          </footer>
        </div>
      </Container>
    </div>
  )
}
